import { motion } from "framer-motion";
import { MapPin } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

const stats = [
  { value: "01", label: "Launch city — Albany, NY" },
  { value: "100%", label: "Drivers background-checked" },
  { value: "518", label: "Proudly built in the Capital Region" },
];

export default function About() {
  return (
    <section id="about" className="relative py-28 md:py-36 border-t border-white/10 scroll-mt-20" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: EASE }}
            className="lg:col-span-5 relative"
          >
            <div className="relative rounded-3xl overflow-hidden border border-white/10">
              <img
                src="https://images.unsplash.com/photo-1518235506717-e1ed3306a89b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHw0fHxhbGJhbnklMjBuZXclMjB5b3JrJTIwY2l0eSUyMHNreWxpbmV8ZW58MHx8fHwxNzg5MTQyNjI0fDA&ixlib=rb-4.1.0&q=85"
                alt="Albany, New York cityscape"
                className="w-full h-[420px] md:h-[520px] object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 inline-flex items-center gap-2 rounded-full bg-black/60 backdrop-blur-md border border-white/15 px-4 py-2">
                <MapPin className="w-4 h-4 text-brand-yellow" />
                <span className="text-xs font-semibold tracking-[0.15em] uppercase">Albany, New York</span>
              </div>
            </div>
            <div className="absolute -bottom-8 -right-4 md:-right-8 w-40 h-40 bg-brand-yellow/20 rounded-full blur-[70px] pointer-events-none" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.15 }}
            className="lg:col-span-7"
          >
            <p className="text-xs font-bold tracking-[0.3em] uppercase text-zinc-500 mb-5">Our story</p>
            <h2 className="font-display font-semibold tracking-tight leading-[1.05] text-4xl sm:text-5xl mb-10">
              A rideshare that <span className="text-brand-yellow">actually belongs</span> to its city.
            </h2>

            <div className="space-y-6 text-zinc-400 leading-relaxed text-base md:text-lg max-w-2xl">
              <p data-testid="about-story-1">
                <span className="font-display font-semibold text-5xl text-brand-yellow float-left mr-3 leading-[0.85] mt-1">E</span>
                zRueda started with a simple frustration: getting around Albany costs too much
                for riders, while drivers keep too little of every fare. The big apps treat the
                Capital Region like an afterthought. We don't.
              </p>
              <p data-testid="about-story-2">
                We're launching in one city — Albany, New York — and doing it right. That means
                fares built for local commutes, earnings that respect local drivers, and support
                from people who actually know the difference between Lark Street and Western Ave.
              </p>
              <p data-testid="about-story-3">
                Every trip on EzRueda keeps money circulating in the 518. Riders save. Drivers
                earn. Albany moves. That's the whole idea.
              </p>
            </div>

            <div className="grid grid-cols-3 gap-6 mt-14 border-t border-white/10 pt-10" data-testid="about-stats">
              {stats.map((s, i) => (
                <motion.div
                  key={s.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7, ease: EASE, delay: 0.2 + i * 0.1 }}
                >
                  <p className="font-display font-semibold text-3xl md:text-5xl text-white tracking-tight">
                    {s.value}
                  </p>
                  <p className="text-xs md:text-sm text-zinc-500 mt-2 leading-snug">{s.label}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
